/**
 * React Query hooks for Individuals/Client data
 * Provides cached, optimized access to individual records
 */
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  getIndividuals,
  getIndividualById,
  getIndividualDetailsComposite,
  getIndividualDetailsByClientId,
  createIndividual,
  updateIndividual,
  deleteIndividual,
} from '../services/individualsService';
import { IndividualFilters } from '../types/individual';

/**
 * Hook to fetch a list of individuals with optional filters and pagination
 * Automatically caches and deduplicates requests
 */
export function useIndividuals(filters?: IndividualFilters & { enabled?: boolean }) {
  const { enabled, ...queryFilters } = filters || {};
  // Normalize filters by removing undefined values to ensure consistent query key serialization
  // Using Partial<IndividualFilters> since filtering may result in fewer properties
  const normalizedFilters: Partial<IndividualFilters> = Object.fromEntries(
    Object.entries(queryFilters).filter(([, value]) => value !== undefined)
  );
  return useQuery({
    queryKey: ['individuals', normalizedFilters],
    queryFn: () => getIndividuals(queryFilters),
    enabled: enabled !== false, // Only fetch if enabled is not explicitly false
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 min
    gcTime: 10 * 60 * 1000, // 10 minutes - time until unused cached data is garbage collected
    refetchOnWindowFocus: true, // Refetch when user returns to tab (background refetching)
    // Keep the previous page/search results on screen while the next query
    // loads. Without this, changing the search term or page swaps the query
    // key to one with no cached data, so `data` becomes undefined and
    // `isLoading` flips true — which blanks any UI gated on it (e.g. the whole
    // ClientListPage) on every keystroke. keepPreviousData holds the last
    // results until the new ones arrive, so the list updates in place.
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to fetch a single individual by ID
 * Automatically disabled if ID is not provided
 */
export function useIndividual(id: number | string | undefined) {
  return useQuery({
    queryKey: ['individual', id],
    queryFn: () => getIndividualById(Number(id!)),
    enabled: !!id, // Only fetch if ID exists
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000, // React Query v5 uses gcTime instead of cacheTime
  });
}

/**
 * Hook to fetch composite individual details (individual + CANS eligibility + assessments)
 * This is faster than making 3 separate queries
 * Automatically disabled if ID is not provided
 */
export function useIndividualDetails(id: number | string | undefined) {
  return useQuery({
    queryKey: ['individual-details', id],
    queryFn: () => getIndividualDetailsComposite(Number(id!)),
    enabled: !!id, // Only fetch if ID exists
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to fetch composite individual details by ClientID (user-facing ID like 'C000031')
 * First resolves clientID to individualID, then fetches full details
 * Automatically disabled if clientId is not provided
 */
export function useIndividualDetailsByClientId(clientId: string | undefined) {
  return useQuery({
    queryKey: ['individual-details-by-client', clientId],
    queryFn: () => getIndividualDetailsByClientId(clientId!),
    enabled: !!clientId, // Only fetch if clientId exists
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to create a new individual
 * Uses optimistic updates for better perceived performance
 */
export function useCreateIndividual() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createIndividual,
    onMutate: async (newIndividual) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['individuals'] });

      // Snapshot the previous value
      const previousIndividuals = queryClient.getQueriesData({ queryKey: ['individuals'] });

      // Optimistically update the cache
      queryClient.setQueriesData({ queryKey: ['individuals'] }, (old: any) => {
        if (!old?.data) {return old;}
        return {
          ...old,
          data: [...old.data, newIndividual],
          pagination: {
            ...old.pagination,
            totalItems: old.pagination.totalItems + 1
          }
        };
      });

      // Return context with snapshot
      return { previousIndividuals };
    },
    onError: (_err, _newIndividual, context) => {
      // Rollback to previous state on error
      if (context?.previousIndividuals) {
        context.previousIndividuals.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      // Invalidate and refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['individuals'] });
    },
  });
}

/**
 * Hook to update an existing individual
 * Uses optimistic updates for better perceived performance
 */
export function useUpdateIndividual() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      updateIndividual(id, data),
    onMutate: async ({ id, data: updateData }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['individual', id] });
      await queryClient.cancelQueries({ queryKey: ['individuals'] });

      // Snapshot previous values
      const previousIndividual = queryClient.getQueryData(['individual', id]);
      const previousIndividuals = queryClient.getQueriesData({ queryKey: ['individuals'] });

      // Optimistically update individual cache
      queryClient.setQueryData(['individual', id], (old: any) => {
        if (!old) {return old;}
        return { ...old, ...updateData };
      });

      // Optimistically update list cache
      queryClient.setQueriesData({ queryKey: ['individuals'] }, (old: any) => {
        if (!old?.data) {return old;}
        return {
          ...old,
          data: old.data.map((item: any) => {
            const itemId = item.IndividualID || item.individualID;
            if (itemId === id) {
              return { ...item, ...updateData };
            }
            return item;
          })
        };
      });

      return { previousIndividual, previousIndividuals };
    },
    onError: (_err, variables, context) => {
      // Rollback on error
      if (context?.previousIndividual) {
        queryClient.setQueryData(['individual', variables.id], context.previousIndividual);
      }
      if (context?.previousIndividuals) {
        context.previousIndividuals.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (data, variables) => {
      // Update with real server response
      queryClient.setQueryData(['individual', variables.id], data);
      // Invalidate list to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['individuals'] });
    },
  });
}

/**
 * Hook to delete an individual
 * Removes from cache after deletion
 */
export function useDeleteIndividual() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteIndividual,
    onSuccess: (_, deletedId) => {
      // Remove individual from cache
      queryClient.removeQueries({ queryKey: ['individual', deletedId] });

      // Invalidate and refetch list
      queryClient.invalidateQueries({ queryKey: ['individuals'] });
    },
  });
}

/**
 * Hook to search individuals by query
 * Includes debouncing logic
 */
export function useSearchIndividuals(
  searchQuery: string,
  page: number = 1,
  pageSize: number = 20
) {
  return useQuery({
    queryKey: ['individuals', 'search', searchQuery, page, pageSize],
    queryFn: () =>
      getIndividuals({
        searchQuery,
        page,
        pageSize,
      } as IndividualFilters),
    enabled: searchQuery.length >= 2, // Only search after 2+ characters
    staleTime: 2 * 60 * 1000, // 2 minutes for search results
    placeholderData: keepPreviousData, // keep prior matches visible while the next query loads
  });
}

